import { App, Form, Input, Modal } from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { getBillEmailPreview, sendBillEmail } from "../../api/entryApi";
import type { BillEntryRow, EmailPreview } from "./billEntryTypes";

export function BillEmailPreviewModal({ row, onClose }: { row: BillEntryRow | null; onClose: () => void }) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const id = row?.id;
  const preview = useQuery({
    queryKey: ["bill-email-preview", id],
    enabled: !!id,
    queryFn: () => getBillEmailPreview<EmailPreview>(id!)
  });
  const sendMutation = useMutation({
    mutationFn: (values: { to: string; subject: string; body: string }) => sendBillEmail(id!, values),
    onSuccess: () => {
      message.success("Email sent");
      onClose();
    },
    onError: (err: any) => message.error(err?.response?.data?.message ?? "Unable to send email")
  });

  useEffect(() => {
    if (!preview.data) return;
    form.setFieldsValue({
      to: preview.data.to,
      subject: preview.data.subject,
      body: preview.data.body
    });
  }, [form, preview.data]);

  const body = Form.useWatch("body", form) ?? preview.data?.body ?? "";

  return (
    <Modal
      open={!!row}
      title={`Email preview${row?.billNo ? `: ${row.billNo}` : ""}`}
      width={900}
      onCancel={onClose}
      okText="Send Email"
      okButtonProps={{ loading: sendMutation.isPending }}
      onOk={async () => sendMutation.mutateAsync(await form.validateFields())}
    >
      <Form form={form} layout="vertical">
        <div className="bill-email-compose">
          <Form.Item name="to" label="To" rules={[{ required: true, message: "Recipient email is required" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="subject" label="Subject" rules={[{ required: true, message: "Subject is required" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="body" label="Body" rules={[{ required: true, message: "Body is required" }]}>
            <Input.TextArea rows={6} />
          </Form.Item>
        </div>
      </Form>
      <div className="bill-email-preview" dangerouslySetInnerHTML={{ __html: body }} />
    </Modal>
  );
}
